// Howie Knowledge Graph — Galaxy View
// Renders ~595 canonical nodes as a force-directed galaxy clustered by domain.

import React, { useRef, useState, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useHowieGalaxySimulation } from './hooks/useHowieGalaxySimulation';
import type { Canonical, CanonicalLink } from './types';
import { BG_COLOR, DOMAIN_COLORS, DOMAIN_LABELS, TEXT_SECONDARY } from './constants';

interface HowieGalaxyViewProps {
  canonicals: Canonical[];
  canonicalDomainIndex: Map<string, number>;
  canonicalLinks: CanonicalLink[];
  onSelectCanonical: (canonicalId: string) => void;
}

const controlBtnStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  background: 'rgba(36, 53, 84, 0.5)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: TEXT_SECONDARY,
};

export const HowieGalaxyView: React.FC<HowieGalaxyViewProps> = ({
  canonicals,
  canonicalDomainIndex,
  canonicalLinks,
  onSelectCanonical,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const { svgRef, resetZoom, zoomIn, zoomOut } = useHowieGalaxySimulation({
    canonicals,
    canonicalDomainIndex,
    canonicalLinks,
    width: dimensions.width,
    height: dimensions.height,
    onNodeClick: onSelectCanonical,
  });

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', background: BG_COLOR }}
    >
      <svg ref={svgRef} width="100%" height="100%" style={{ display: 'block' }} />

      {/* Zoom controls */}
      <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button style={controlBtnStyle} onClick={zoomIn} title="Zoom in" aria-label="Zoom in">
          <ZoomIn size={18} />
        </button>
        <button style={controlBtnStyle} onClick={zoomOut} title="Zoom out" aria-label="Zoom out">
          <ZoomOut size={18} />
        </button>
        <button style={controlBtnStyle} onClick={resetZoom} title="Reset zoom" aria-label="Reset zoom">
          <Maximize2 size={18} />
        </button>
      </div>

      {/* Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          background: 'rgba(10, 10, 15, 0.7)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 8,
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
          Domains
        </span>
        {DOMAIN_COLORS.map((color, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 4px ${color}88` }} />
            <span style={{ fontSize: 10, color: TEXT_SECONDARY }}>{DOMAIN_LABELS[idx]}</span>
          </div>
        ))}
        <div style={{ marginTop: 3, fontSize: 9, color: TEXT_SECONDARY + '80' }}>
          {canonicals.length} concepts · click to explore
        </div>
      </div>
    </div>
  );
};
